import React, { useState, useEffect, useContext, useMemo } from 'react';

import { Row, Col, Modal } from 'antd';

import styled from 'styled-components';

import { addProjectLog } from '@libs/NwLogs';
import { getUserName } from '@libs/NwUsers';
import { getCustomFieldValue } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';
import { useProjectContext } from '@contexts/ProjectContext';

import { useAddTag, useRemoveTag, useDeleteProject } from '@hooks/API/Project/UseProject';
import useDepartmentName from '@hooks/CachedLists/UseDepartmentName';

import CustomerDrawer from '@components/ContactDetails/CustomerDetails/Drawer/CustomerDrawer';
import { Loading } from '@components/Gui/Loading';
import { NwLinkButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import ProjectEditDrawer from '@components/Project/ProjectEditDrawer/ProjectEditDrawer';
import ProjectEventsOverview from '@components/Project/ProjectOverview/ProjectEventsOverview';
import ProjectModels from '@components/Project/ProjectOverview/ProjectModels';
import ObjectTagPicker from '@components/TagPicker/ObjectTagPicker';
import ProjectPageTaskList from '@components/TaskList/ProjectPageTaskList';
import UserIcon from '@components/UserIcon/UserIcon';

import ProjectChangeBookerDrawer from '../ProjectChangeBookerDrawer';
import ProjectChangeCustomer from '../ProjectEditDrawer/ProjectChangeCustomer';
import ProjectEstimatedFees from './ProjectEstimatedFees';
import ProjectPackagesOverview from './ProjectPackagesOverview';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const EventFieldsTable = styled.table`
    list-style-type: none;
    margin-left: 8px;
    .title-cell {
        border-right: ${(props) => props.theme.DefaultBorder};
        padding-right: 2rem;
        min-width: 10rem;
        font-size: ${(props) => props.theme.FontSizeSm};
        text-transform: uppercase;
        vertical-align: top;
        padding-top: 0.5rem;
    }
    .content-cell {
        padding-left: 20px;
        font-size: 0.95rem;
    }
`;

const ProjectOverviewContainer = styled.div`
    margin-bottom: 16px;
    .project-overview-left {
        padding-left: 1rem;
    }
    .project-overview-right {
        padding-left: 1rem;
    }

    .internal-card {
        margin-top: 3rem;
        width: 100%;
        padding-right: 1rem;

        &.first-line {
            margin-top: 0.5rem;
        }

        .nw-empty-state {
            .ant-empty.ant-empty-normal {
                margin-top: 1rem;
                margin-bottom: 0;
            }
        }

        .header {
            border-bottom: ${(props) => props.theme.DefaultBorder};
            display: flex;
            align-items: center;
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
            justify-content: space-between;

            .title-container {
                display: flex;
                align-items: center;

                .icon {
                    font-size: 18px;
                }
                .title {
                    font-size: 18px;
                    font-weight: bold;
                    margin-left: 8px;
                }
            }
        }
        .content {
            .project-booker-card-content {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;

                .project-booker-card-content-avatar {
                    padding-right: 2rem;
                }

                .project-booker-card-content-text {
                    flex: 1 1 0;
                    p {
                        margin: 0 0 0.25rem 0;
                    }
                }
                .project-booker-card-content-actions {
                    padding-left: 2rem;
                }
            }
        }
        .todo-item {
            border-bottom: ${(props) => props.theme.LighterBorder};
        }
    }

    .project-info {
        border-right: ${(props) => props.theme.DefaultBorder};
    }
`;

const ProjectDetail = styled(Row)`
    .ant-col:first-child {
        border-right: ${(props) => props.theme.DefaultBorder};
    }

    .ant-typography-expand {
        color: ${(props) => props.theme.PrimaryColor};
    }
`;

const ProjectOverview = ({ onMoveToEvent, afterProjectDelete }) => {
    const { projectId, projectData: currentProject } = useProjectContext();
    const allowBookerChange = true;

    const { mutateAsync: onAddTag } = useAddTag();
    const { mutateAsync: onRemoveTag } = useRemoveTag();
    const { mutateAsync: onDeleteProject } = useDeleteProject();

    const [isEditDrawer, setIsEditDrawer] = useState(false);
    const [isChangeCustomerDrawer, setIsChangeCustomerDrawer] = useState(false);
    const [customFields, setCustomFields] = useState([]);
    const [validCustomFieldsKeys, setValidCustomFieldsKeys] = useState([]);
    const [isContactDrawer, setIsContactDrawer] = useState(false);
    const [openBookerChangeDrawer, setOpenBookerChangeDrawer] = useState(false);
    const { cachedList } = useContext(ListContext);

    const deptname = useDepartmentName(currentProject.DepartmentCreated, currentProject.NetworkAgencyCreated);

    const usersList = cachedList.users.items;

    useEffect(() => {
        const projectCustomFields = cachedList.customFields.items.Project;
        setCustomFields(projectCustomFields);
        setValidCustomFieldsKeys(projectCustomFields.filter((field) => !field.Disabled).map((field) => field.Name));
    }, [cachedList]);

    useEffect(() => {
        if (currentProject) {
            addProjectLog(currentProject);
        }
    }, [currentProject]);

    const projectCategory = useMemo(() => {
        if (currentProject) {
            return cachedList.projectCategories.items.find(
                (category) => category.ID === currentProject.ProjectCategoryID
            );
        }
        return null;
    }, [currentProject]);

    const handleEditProject = () => {
        setIsEditDrawer(true);
    };

    const handleCloseEditDrawer = () => {
        setIsEditDrawer(false);
    };

    const handleCloseChangeCustomerDrawer = () => {
        setIsChangeCustomerDrawer(false);
    };

    const handleAddTag = async (tag) => {
        await onAddTag({ tag, projectId });
    };

    const handleRemoveTag = async (tag) => {
        await onRemoveTag({ tag, projectId });
    };

    const handleCloseContactDrawer = () => {
        setIsContactDrawer(false);
    };

    const handleDeleteProject = async () => {
        try {
            await onDeleteProject({ projectId: projectId });
            afterProjectDelete();
        } catch (e) {
            Modal.error({
                title: e.message,
            });
        }
    };

    const onOpenBookerChangeDrawer = () => {
        setOpenBookerChangeDrawer(true);
    };

    const onCloseBookerChangeDrawer = () => {
        setOpenBookerChangeDrawer(false);
    };

    return (
        <ProjectOverviewContainer>
            {!currentProject ? (
                <Loading />
            ) : (
                <>
                    <NwCard>
                        <Row gutter={32}>
                            <Col className="project-overview-left" lg={12} md={12} sm={24} xs={24}>
                                <div className="internal-card first-line">
                                    <div className="header">
                                        <div className="title-container">
                                            <NwIcon className="icon" icon={byPrefixAndName.fasl['info-circle']} />
                                            <div className="title">Info</div>
                                        </div>
                                        <NwDropDownMenu
                                            actions={[
                                                {
                                                    name: '',
                                                    key: 'edit',
                                                    pinned: true,
                                                    icon: byPrefixAndName.fasl['pen'],
                                                    action: () => handleEditProject(),
                                                },
                                                {
                                                    name: 'delete',
                                                    type: 'delete',
                                                    key: 'delete',
                                                    icon: byPrefixAndName.fasl['trash-alt'],
                                                    confirmationPlacement: 'left',
                                                    action: () => handleDeleteProject(),
                                                },
                                                {
                                                    name: 'change customer',
                                                    key: 'change customer',
                                                    icon: byPrefixAndName.fasl['user-pen'],
                                                    action: () => setIsChangeCustomerDrawer(true),
                                                },
                                            ]}
                                        />
                                    </div>
                                    <div className="content">
                                        <ProjectDetail gutter={16}>
                                            <EventFieldsTable>
                                                <tbody>
                                                    <tr>
                                                        <td className="title-cell">
                                                            <strong>Project Client</strong>
                                                        </td>
                                                        <td className="content-cell">
                                                            <strong
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setIsContactDrawer(true)}
                                                            >
                                                                {currentProject.Contact
                                                                    ? currentProject.Contact.Name
                                                                    : 'n.d.'}
                                                            </strong>
                                                            <NwLinkButton
                                                                size="small"
                                                                style={{ marginLeft: '0.5rem' }}
                                                                onClick={() => setIsChangeCustomerDrawer(true)}
                                                                label="change"
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="title-cell">Project ID</td>
                                                        <td className="content-cell">{currentProject.ID}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="title-cell">Product</td>
                                                        <td className="content-cell">
                                                            {currentProject.Product || 'n.d.'}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="title-cell">Category</td>
                                                        <td className="content-cell">
                                                            {projectCategory ? projectCategory.Name : ''}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="title-cell">Description</td>
                                                        <td className="content-cell" style={{ paddingRight: '4rem' }}>
                                                            <NwEllipsis
                                                                text={currentProject.Description || ''}
                                                                rows={5}
                                                                expandable
                                                                symbol="more"
                                                            />
                                                            {/* {currentProject.Description || ''}</td> */}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="title-cell">P.O. Number</td>
                                                        <td className="content-cell" style={{ paddingRight: '4rem' }}>
                                                            {currentProject.PONumber || 'n.d.'}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="title-cell">Accounting Note</td>
                                                        <td className="content-cell" style={{ paddingRight: '4rem' }}>
                                                            <NwEllipsis
                                                                text={currentProject.AccountingNote || ''}
                                                                rows={5}
                                                                expandable
                                                                symbol="more"
                                                            />
                                                        </td>
                                                    </tr>
                                                    {Object.keys(currentProject.CustomFields).map((key, index) => {
                                                        if (
                                                            validCustomFieldsKeys &&
                                                            validCustomFieldsKeys.includes(key)
                                                        ) {
                                                            return (
                                                                <tr key={`key-${index}`}>
                                                                    <td className="title-cell">
                                                                        {key.replace(/_/g, ' ')}
                                                                    </td>
                                                                    <td className="content-cell">
                                                                        {getCustomFieldValue(
                                                                            key,
                                                                            currentProject.CustomFields[key],
                                                                            customFields
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    })}
                                                </tbody>
                                            </EventFieldsTable>
                                        </ProjectDetail>
                                    </div>
                                </div>
                                <div className="internal-card">
                                    <div className="header">
                                        <div className="title-container">
                                            <NwIcon className="icon" icon={byPrefixAndName.fasl['tag']} />
                                            <div className="title">Tags</div>
                                        </div>
                                    </div>
                                    <div className="content">
                                        <ObjectTagPicker
                                            itemType="Event"
                                            tags={currentProject.Tags}
                                            onAddTag={handleAddTag}
                                            onRemoveTag={handleRemoveTag}
                                            stripMaxWidth="100%"
                                            stripShowAll
                                        />
                                    </div>
                                </div>
                                <div className="internal-card">
                                    <div className="header">
                                        <div className="title-container">
                                            <NwIcon className="icon" icon={byPrefixAndName.fasl['user-vneck']} />
                                            <div className="title">Booker</div>
                                        </div>
                                    </div>
                                    <div className="content">
                                        <div className="project-booker-card-content">
                                            <div className="project-booker-card-content-avatar">
                                                <UserIcon
                                                    shape="square"
                                                    size={72}
                                                    userId={currentProject.UserCreated}
                                                />
                                            </div>
                                            <div className="project-booker-card-content-text">
                                                <p>
                                                    <strong>
                                                        {getUserName(currentProject.UserCreated, usersList)}
                                                    </strong>
                                                </p>
                                                <p>Department: {deptname}</p>
                                            </div>
                                            {allowBookerChange && (
                                                <div className="project-booker-card-content-actions">
                                                    <NwIcon
                                                        primary
                                                        icon={byPrefixAndName.fasl['user-pen']}
                                                        onClick={onOpenBookerChangeDrawer}
                                                        pointer
                                                    />
                                                    &nbsp;
                                                    <NwLinkButton
                                                        onClick={onOpenBookerChangeDrawer}
                                                        label="assign to another booker"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <ProjectModels projectId={projectId} />
                                <ProjectEstimatedFees projectId={projectId} />
                            </Col>
                            <Col className="project-overview-right" lg={12} md={12} sm={24} xs={24}>
                                <ProjectPageTaskList projectId={projectId} />
                                <ProjectEventsOverview projectId={projectId} onMoveToEvent={onMoveToEvent} />
                                <ProjectPackagesOverview projectId={projectId} />
                            </Col>
                        </Row>
                    </NwCard>
                    {isEditDrawer && (
                        <ProjectEditDrawer
                            project={currentProject}
                            customFields={customFields}
                            onClose={handleCloseEditDrawer}
                        />
                    )}
                    {isChangeCustomerDrawer && (
                        <ProjectChangeCustomer project={currentProject} onClose={handleCloseChangeCustomerDrawer} />
                    )}
                    {isContactDrawer && (
                        <CustomerDrawer customerID={currentProject.Contact.ID} onClose={handleCloseContactDrawer} />
                    )}
                </>
            )}
            {openBookerChangeDrawer && (
                <ProjectChangeBookerDrawer project={currentProject} onClose={onCloseBookerChangeDrawer} />
            )}
        </ProjectOverviewContainer>
    );
};

export default ProjectOverview;
